import { useState } from "react";
import { Link } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { PageHeader } from "@/components/StatCard";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Check, X, ChevronRight, Bot, UserCheck, ShieldX, Archive } from "lucide-react";
import { Loading, Empty, Section, useFetch, dt } from "./_shared";

function methodOf(r) {
  const by = (r.coach_verification || {}).approved_by;
  if (by === "ai") return "automatisch";
  if (by === "admin") return "manuell";
  if (by === "grandfathered") return "bestand";
  return "—";
}

function Row({ r, right }) {
  const cv = r.coach_verification || {};
  const conf = cv.ai_verdict?.confidence != null ? `${Math.round(cv.ai_verdict.confidence * 100)}%` : null;
  return (
    <div className="flex items-center gap-3 rounded-2xl border border-border bg-card p-4" data-testid={`admin-verify-row-${r.id}`}>
      <Link to={`/app/admin/users/${r.id}`} className="flex min-w-0 flex-1 items-center gap-3 hover:opacity-80" data-testid={`admin-verify-open-${r.id}`}>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium text-foreground">
            {r.name || "—"}
            <span className="ml-1 rounded bg-secondary px-1.5 text-[10px] text-muted-foreground">{r.role}</span>
          </p>
          <p className="truncate text-xs text-muted-foreground">
            {r.email || "(keine E-Mail)"}
            {cv.submitted_at ? ` · eingereicht ${dt(cv.submitted_at)}` : ""}
            {cv.ai_verdict?.subject_area ? ` · ${cv.ai_verdict.subject_area}` : ""}
            {conf ? ` · KI ${conf}` : ""}
          </p>
        </div>
        <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
      </Link>
      {right}
    </div>
  );
}

function MethodBadge({ method }) {
  const map = {
    automatisch: { cls: "bg-sky-100 text-sky-700", Icon: Bot },
    manuell: { cls: "bg-emerald-100 text-emerald-700", Icon: UserCheck },
    bestand: { cls: "bg-amber-100 text-amber-700", Icon: Archive },
  };
  const { cls, Icon } = map[method] || { cls: "bg-secondary text-muted-foreground", Icon: Archive };
  return (
    <span className={`inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium ${cls}`}>
      <Icon className="h-3 w-3" /> {method}
    </span>
  );
}

function OpenTable() {
  const { data, loading, reload } = useFetch("/admin/coach-verify/queue?status=pending_admin");
  const [busy, setBusy] = useState(null);
  const rows = data?.rows || [];

  const decide = async (id, approve) => {
    setBusy(id);
    try {
      await api.post(`/admin/coach-verify/${id}/decide`, { approve, reason: approve ? null : "Qualifikation unzureichend" });
      toast.success(approve ? "Freigegeben." : "Abgelehnt.");
      await reload();
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(null); }
  };

  return (
    <Section title={`Offene Verifizierungen${rows.length ? ` · ${rows.length}` : ""}`} testId="admin-verify-open">
      {loading ? <Loading /> : rows.length === 0 ? <Empty text="Keine offenen Verifizierungen." /> : (
        <div className="space-y-2">
          {rows.map((r) => (
            <Row key={r.id} r={r} right={
              <div className="flex shrink-0 gap-1.5">
                <Button size="sm" variant="outline" className="rounded-full text-emerald-600" disabled={busy === r.id} onClick={() => decide(r.id, true)} data-testid={`admin-verify-approve-${r.id}`}><Check className="h-4 w-4" /></Button>
                <Button size="sm" variant="outline" className="rounded-full text-rose-600" disabled={busy === r.id} onClick={() => decide(r.id, false)} data-testid={`admin-verify-reject-${r.id}`}><X className="h-4 w-4" /></Button>
              </div>
            } />
          ))}
        </div>
      )}
    </Section>
  );
}

function CompletedTable() {
  const { data, loading } = useFetch("/admin/coach-verify/queue?status=approved");
  const [filter, setFilter] = useState("all");
  const rows = data?.rows || [];
  const nAuto = rows.filter((r) => methodOf(r) === "automatisch").length;
  const nManual = rows.filter((r) => methodOf(r) === "manuell").length;
  const nBestand = rows.filter((r) => methodOf(r) === "bestand").length;
  const filtered = filter === "all" ? rows : rows.filter((r) => methodOf(r) === filter);

  const chipDefs = [["all", `Alle · ${rows.length}`], ["manuell", `Manuell · ${nManual}`], ["automatisch", `Automatisch · ${nAuto}`]];
  if (nBestand) chipDefs.push(["bestand", `Bestand · ${nBestand}`]);
  const chips = (
    <div className="flex flex-wrap gap-1" data-testid="admin-verify-filter">
      {chipDefs.map(([k, l]) => (
        <button key={k} onClick={() => setFilter(k)} data-testid={`admin-verify-filter-${k}`}
          className={`rounded-full px-2.5 py-1 text-[11px] font-medium transition-colors ${filter === k ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground hover:bg-accent"}`}>{l}</button>
      ))}
    </div>
  );

  return (
    <Section title={`Erledigte Verifizierungen${rows.length ? ` · ${rows.length}` : ""}`} testId="admin-verify-completed" action={chips}>
      {loading ? <Loading /> : filtered.length === 0 ? <Empty text="Keine erledigten Verifizierungen in dieser Ansicht." /> : (
        <div className="space-y-2">
          {filtered.map((r) => {
            const cv = r.coach_verification || {};
            const m = methodOf(r);
            return (
              <Row key={r.id} r={r} right={
                <div className="flex shrink-0 flex-col items-end gap-0.5">
                  <MethodBadge method={m} />
                  {cv.approved_at && <span className="text-[10px] text-muted-foreground">{dt(cv.approved_at)}</span>}
                </div>
              } />
            );
          })}
        </div>
      )}
    </Section>
  );
}

function RejectedTable() {
  const { data, loading } = useFetch("/admin/coach-verify/queue?status=rejected");
  const rows = data?.rows || [];
  return (
    <Section title={`Abgelehnte Verifizierungen${rows.length ? ` · ${rows.length}` : ""}`} testId="admin-verify-rejected">
      {loading ? <Loading /> : rows.length === 0 ? <Empty text="Keine abgelehnten Verifizierungen." /> : (
        <div className="space-y-2">
          {rows.map((r) => {
            const cv = r.coach_verification || {};
            const reason = cv.admin_verdict?.reason;
            return (
              <Row key={r.id} r={r} right={
                <div className="flex shrink-0 flex-col items-end gap-0.5">
                  <span className="inline-flex items-center gap-1 rounded-full bg-rose-100 px-2 py-0.5 text-[10px] font-medium text-rose-700"><ShieldX className="h-3 w-3" /> abgelehnt</span>
                  {cv.rejected_at && <span className="text-[10px] text-muted-foreground">{dt(cv.rejected_at)}</span>}
                  {reason && <span className="max-w-[160px] truncate text-[10px] text-muted-foreground" title={reason}>{reason}</span>}
                </div>
              } />
            );
          })}
        </div>
      )}
    </Section>
  );
}

export default function Verify() {
  return (
    <div className="space-y-5" data-testid="admin-verify-page">
      <PageHeader eyebrow="Admin" title="Coach-Verifizierung" subtitle="Eingereichte Qualifikationen prüfen. Klick auf einen Nutzer öffnet das Profil mit dem Zertifikat." />
      <OpenTable />
      <CompletedTable />
      <RejectedTable />
    </div>
  );
}
